import { validate as validateJsonSchema } from 'jsonschema';
import ipaddr from 'ipaddr.js';
import { compareVersions } from 'compare-versions';
import { isEmailValid, isPasswordValid } from '../../utils';

import {
    PACKET_FWD_ADDRESS_FIELDS,
    PROTECTED_DOMAIN_FIELDS,
    NODE_FIELDS,
    SSO_FIELDS,
    FAIL_CLOSE_FIELDS,
    SELECTIVE_FWD_FIELDS,
    AVAILABILITY_ZONE_FIELDS,
    COMMON_FIELDS
} from './fields';
import {
    PACKET_FWD_ADDRESS_OPTIONS,
    PACKET_FWD_PROTOCOL_OPTIONS,
    SYSLOG_MSG_TYPES,
    SHARED_KEY,
    UNASSIGNED_KEY,
    DCS_WITH_DOMAIN_KEY,
    WAF_COMPLIANT_STRING_PATTERN,
    isValidPhoenixUrl
} from '@silverfort-nodelibs/common/utils/consts';
import { isEmpty } from '@silverfort-nodelibs/common/utils/common';

const { AD_PROTOCOLS } = FAIL_CLOSE_FIELDS;
const { SELECTIVE_FORWARDING: SELECTIVE_FORWARDING_FIELD } = SELECTIVE_FWD_FIELDS;

const isNumericPattern = /^\d+$/;
const isEnglishUserName = /^[A-Za-z0-9@._-]+$/;
const ipPattern =
    /^(([1-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.)(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){2}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
const domain_pattern =
    /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/;
const ipNoZeroPattern =
    /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([1-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
const ipAndMaskPattern = /((^|\.)((25[0-5])|(2[0-4]\d)|(1\d\d)|([1-9]?\d))){4}\/(?:\d|[12]\d|3[012])$/;
const sbConnectionStringPattern =
    /^Endpoint=sb:\/\/[^;]+?;SharedAccessKeyName=[^;]+?;SharedAccessKey=[^;]+?;EntityPath=[^;]+?$/i;
const urlPattern = /^(https?|ftp):\/\/(-\.)?([^\s/?.#]+\.?)+(\/[^\s]*)?$/i;
const httpPattern = /^http:\/\/(-\.)?([^\s/?.#]+\.?)+(\/[^\s]*)?$/i;
const httpsPattern = /^https:\/\/(-\.)?([^\s/?.#]+\.?)+(\/[^\s]*)?$/i;
// ldap/s allow for FQDN and not just IP
const ldapUrlPattern = /^^(ldap|ldaps):\/\/(.+)$/i;
const httpIpPortPattern =
    /(?:((http|https):\/\/))?(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(?::\d+)?\b/;

const VALIDATE_NO_UNASSIGNED_DCS_MSG = 'Before saving, assign all DCs from domains: ';
const MUST_HAVE_WALL_IF_DC_MSG =
    'If an availability zone is assigned a domain controller, it must also be assigned a node.';
const MAX_ONE_EMPTY_TAB_MSG = 'You may only have one empty availability zone.';
const VALID_IP_ERROR = 'must contain a valid IP address';
const VALID_DOMAIN_ERROR = 'must contain a valid domain name';
const VALID_DOMAIN_OR_IP_ERROR = 'must contain a valid domain name or IP address';
const VALID_DR_DURATION = 'must be a number between 0.5 to 99999';

export const validate = (value, rules, fieldName = '') => {
    if (!rules || (!rules.required && value === '')) {
        return null;
    }

    if (rules.required && (!value || value.toString().trim() === '')) {
        return `${fieldName} is required`;
    }
    if (rules.defined && value === undefined) {
        return `${fieldName} is required`;
    }
    if (rules.positiveNumber && value !== undefined && value <= 0) {
        return `${fieldName} should be positive number`;
    }
    if (rules.differentFrom && value && value === rules.differentFrom) {
        return `${fieldName} should be changed`;
    }
    if (rules.notWhitespace && value && value.toString().trim() === '') {
        return `${fieldName} can't contain whitespace only`;
    }
    if (rules.notContainWhitespace && value && value.includes(' ')) {
        return `${fieldName} can't contain whitespace at all`;
    }
    if (rules.notEmptyGuids && value && value.length === 0) {
        return `Entities must contain at least one item`;
    }
    if (rules.isNumeric && !isNumericPattern.test(value)) {
        return `${fieldName} must contain number values only`;
    }
    if (rules.isEnglishLettersUsername && !isEnglishUserName.test(value)) {
        return `${fieldName} must contain english characters and number values only`;
    }
    if (rules.maxValue && value && value > rules.maxValue) {
        return `${fieldName} maximum value is ${rules.maxValue}`;
    }
    if (rules.minValue && value && value < rules.minValue) {
        return `${fieldName} minimum value is ${rules.minValue}`;
    }
    if (rules.ip && !ipPattern.test(value)) {
        return `${fieldName} ${VALID_IP_ERROR}`;
    }
    if (rules.domain && !domain_pattern.test(value)) {
        return `${fieldName} ${VALID_DOMAIN_ERROR}`;
    }
    if (rules.domainOrIpArray && value && value.length) {
        for (const valueEntry of value) {
            if (!domain_pattern.test(valueEntry) && !ipPattern.test(valueEntry)) {
                return `${fieldName} ${VALID_DOMAIN_OR_IP_ERROR}`;
            }
        }
    }
    if (rules.domainOrIp && value && !domain_pattern.test(value) && !ipPattern.test(value)) {
        return `${fieldName} ${VALID_DOMAIN_OR_IP_ERROR}`;
    }
    if (rules.ipNoZero && !ipNoZeroPattern.test(value)) {
        return `${fieldName} ${VALID_IP_ERROR} and not end with '.0'`;
    }
    if (rules.ipAndMask && !ipAndMaskPattern.test(value)) {
        return `${fieldName} must contain a valid IP/Mask address`;
    }
    if (rules.ipOrIpAndMask && !ipAndMaskPattern.test(value) && !ipPattern.test(value)) {
        return `${fieldName} must contain a valid IP or IP/Mask address`;
    }
    if (
        rules.ipsOrIpAndMasks &&
        (!Array.isArray(value) ||
            value.some((singleVal) => !ipAndMaskPattern.test(singleVal) && !ipPattern.test(singleVal)))
    ) {
        return `All ${fieldName} values must contain a valid IP or IP/Mask address`;
    }
    if (rules.sbConnectionString && !sbConnectionStringPattern.test(value)) {
        return `${fieldName} must contain a valid ServiceBus Connection String`;
    }
    if (rules.url && value && !urlPattern.test(value)) {
        return `${fieldName} must contain a valid URL`;
    }
    if (rules.httpUrl && value && !httpPattern.test(value)) {
        return `${fieldName} must contain a valid HTTP URL`;
    }
    if (rules.httpsUrl && value && !httpsPattern.test(value)) {
        return `${fieldName} must contain a valid HTTPS URL`;
    }
    if (rules.minLength && value.length < rules.minLength) {
        return `${fieldName} must contain at least ${rules.minLength} characters`;
    }
    if (rules.maxLength && value.length > rules.maxLength) {
        return `${fieldName} has exceeded the limit of ${rules.maxLength} characters`;
    }
    if (rules.ldapUrl && !ldapUrlPattern.test(value)) {
        return `${fieldName} must contain a valid LDAP/S URL`;
    }
    if (rules.email && !isEmailValid(value)) {
        return `${fieldName} must be a valid email`;
    }
    if (rules.userRequired && (!value || !value.user)) {
        return `${fieldName} is required`;
    }
    if (rules.dataRequired && (!value || !value.data)) {
        return `${fieldName} is required`;
    }
    if (rules.noSemicolon && value.includes(';')) {
        return `${fieldName} cannot contain ';'`;
    }
    if (rules.httpIpPort) {
        const match = value.match(httpIpPortPattern);
        if (!match || match[0] !== value) {
            return `${fieldName} must container a valid IP with/without protocol and port`;
        }
    }
    if (rules.isJson) {
        if (value && value !== '') {
            try {
                JSON.parse(value);
            } catch (err) {
                return err.message;
            }
        }
    }
    if (rules.jsonSchema) {
        if (value && value !== '') {
            try {
                const parsedValue = JSON.parse(value);
                if (typeof parsedValue === 'object' && parsedValue !== null && Object.keys(parsedValue).length === 0) {
                    return null;
                }
                const result = validateJsonSchema(parsedValue, rules.jsonSchema);
                if (result.errors.length > 0) {
                    return `Invalid schema: ${result.errors[0].stack}`;
                }
            } catch (err) {
                return err.message;
            }
        }
    }
    if (rules.wafCompliantString) {
        const match = value.match(WAF_COMPLIANT_STRING_PATTERN);
        if (!match || match[0] !== value) {
            return `${fieldName} must not contain any special characters apart from ", ?, -, _ and .`;
        }
    }
    if (rules.validateSSOCallback && value && !value.endsWith('/sso/saml/callback')) {
        return `${fieldName} must end with /sso/saml/callback`;
    }
    if (rules.password && (!value || !isPasswordValid(value))) {
        return 'The password must contain at least 8 characters and include letters, digits, and special characters.';
    }
    if (rules.customValidator) {
        return rules.customValidator(value, fieldName);
    }
    if (rules.validateDrDuration) {
        const floatValue = parseFloat(value.duration);
        if (!value.duration || isNaN(floatValue) || floatValue < 0.5) {
            return VALID_DR_DURATION;
        }
    }
    if (rules.phoenixUrl) {
        const parsedValue = JSON.parse(value);
        if ('PhoenixBaseUrl' in parsedValue && !isValidPhoenixUrl(parsedValue.PhoenixBaseUrl)) {
            return "Phoenix base url does not match url requirements";
        }
    }
};

export const validateSyslogSplunkTimezone = (syslogsServer) => {
    const isSplunk = syslogsServer.type.value.includes(SYSLOG_MSG_TYPES.SPLUNK.value.toString());
    if (isSplunk && !syslogsServer.splunkTimezone.value) {
        syslogsServer.splunkTimezone.error = `${syslogsServer.splunkTimezone.name} is required`;
    } else {
        syslogsServer.splunkTimezone.error = null;
    }
};

export const validateRadiusClients = (radiusClientsField, ipAddressFieldId) => {
    const radiusClients = radiusClientsField.value;
    for (let i = 0; i < radiusClients.length; ++i) {
        const radiusClient1 = radiusClients[i];
        const ip1 = radiusClient1[ipAddressFieldId].value;
        const range1 = [ipaddr.IPv4.parseCIDR(ip1)];
        const networkAddress1 = ipaddr.IPv4.networkAddressFromCIDR(ip1);

        for (let j = 0; j < radiusClients.length; ++j) {
            if (i === j) {
                continue;
            }
            const radiusClient2 = radiusClients[j];
            const ip2 = radiusClient2[ipAddressFieldId].value;
            const range2 = [ipaddr.IPv4.parseCIDR(ip2)];
            const networkAddress2 = ipaddr.IPv4.networkAddressFromCIDR(ip2);

            const match =
                ipaddr.subnetMatch(networkAddress1, range2, false) ||
                ipaddr.subnetMatch(networkAddress2, range1, false);
            if (match) {
                radiusClients[i][ipAddressFieldId].error = `This IP address is overlapping with ${ip2}`;
            }
        }
    }
};

export const validateLdapServerUnique = (domainField, ldapId, backupLdapId) => {
    const domainFields = domainField.fields ? domainField.fields : domainField;
    const ldapField = domainFields[ldapId];
    const backupLdapField = domainFields[backupLdapId];

    const error1 = 'Backup LDAP IP can be filled only if LDAP Server has value';
    const error2 = 'Backup LDAP IP value need to be different from LDAP Server value';
    if (backupLdapField.error === error1 || backupLdapField.error === error2) {
        backupLdapField.error = null;
    }

    if (backupLdapField.value && !ldapField.value) {
        backupLdapField.error = error1;
    } else if (backupLdapField.value && backupLdapField.value === ldapField.value) {
        backupLdapField.error = error2;
    }
};

export const validateNoUnassignedDcs = (validatedState) => {
    let clearError = true;
    if (
        validatedState[SHARED_KEY] &&
        validatedState[SHARED_KEY][UNASSIGNED_KEY] &&
        validatedState[SHARED_KEY][UNASSIGNED_KEY].dcs &&
        validatedState[SHARED_KEY][UNASSIGNED_KEY].dcs.filter(
            (dc) => validatedState[SHARED_KEY][DCS_WITH_DOMAIN_KEY][dc]?.isProtected
        ).length > 0
    ) {
        const domainGuids = [];
        validatedState[SHARED_KEY][UNASSIGNED_KEY].dcs.forEach((dcGuid) => {
            if (validatedState[SHARED_KEY][DCS_WITH_DOMAIN_KEY][dcGuid]) {
                const domainGuid = validatedState[SHARED_KEY][DCS_WITH_DOMAIN_KEY][dcGuid].domain;
                if (domainGuid && !domainGuids.includes(domainGuid)) {
                    domainGuids.push(domainGuid);
                }
            }
        });
        const domainsList = domainGuids.flatMap((domainGuid) => {
            const option = validatedState[SHARED_KEY]['domainOptions'].find(
                (domainOption) => domainOption.value === domainGuid
            );
            return option ? [option.label] : [];
        });

        if (domainsList.length > 0) {
            clearError = false;
            for (const tab of Object.keys(validatedState)) {
                if (tab !== SHARED_KEY) {
                    validatedState[tab].fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].error =
                        VALIDATE_NO_UNASSIGNED_DCS_MSG + domainsList.join(', ');
                }
            }
        }
    }
    if (clearError) {
        for (const tab of Object.keys(validatedState)) {
            if (
                tab !== SHARED_KEY &&
                validatedState[tab].fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].error &&
                validatedState[tab].fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].error.startsWith(
                    VALIDATE_NO_UNASSIGNED_DCS_MSG
                )
            ) {
                validatedState[tab].fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].error = null;
            }
        }
    }
};

export const validateFilledForwarding = (domainField, kerberosFieldId, ntlmFieldId, ldapFieldId, ldapsFieldId) => {
    const domainFields = domainField.fields ? domainField.fields : domainField;
    const kerberosField = domainFields[kerberosFieldId];
    const ntlmField = domainFields[ntlmFieldId];
    const ldapField = domainFields[ldapFieldId];
    const ldapsField = domainFields[ldapsFieldId];
    kerberosField.error = null;
    ntlmField.error = null;
    ldapField.error = null;
    ldapsField.error = null;
    if (!kerberosField.value && !ntlmField.value && !ldapField.value && !ldapsField.value) {
        const error = 'Protected protocols must have at least one protocol enabled';
        kerberosField.error = error;
        ntlmField.error = error;
        ldapField.error = error;
        ldapsField.error = error;
    }
};

export const validateAdaptiveBlock = (autoDenyMfaThresholdField, autoDenyMfaWindowSizeField) => {
    if (autoDenyMfaThresholdField.value && !autoDenyMfaWindowSizeField.value) {
        autoDenyMfaWindowSizeField.error = `Field '${autoDenyMfaWindowSizeField.name}' cannot be empty if '${autoDenyMfaThresholdField.name}' is not empty`;
    } else if (!autoDenyMfaThresholdField.value && autoDenyMfaWindowSizeField.value) {
        autoDenyMfaThresholdField.error = `Field '${autoDenyMfaThresholdField.name}' cannot be empty if '${autoDenyMfaWindowSizeField.name}' is not empty`;
    }
};

export const validateUniqueIdentifier = (field, identifierFieldId) => {
    const applicationIds = new Set();
    for (const azureAdConnector of field.value) {
        const appIdField = azureAdConnector[identifierFieldId];
        if (applicationIds.has(appIdField.value)) {
            const error = `${appIdField.name} must be unique`;
            appIdField.error = error;
        }
        applicationIds.add(appIdField.value);
    }
};

export const validateFilledCustomInsight = (
    customInsightField,
    conditionsFieldId,
    excludeConditionsFieldId,
    usersAndGroupsFieldId,
    excludedUsersAndGroupsFieldId
) => {
    const conditionsField = customInsightField.fields[conditionsFieldId];
    const excludeConditionsField = customInsightField.fields[excludeConditionsFieldId];
    const usersAndGroupsField = customInsightField.fields[usersAndGroupsFieldId];
    const excludedUsersAndGroupsField = customInsightField.fields[excludedUsersAndGroupsFieldId];
    const existingFields = [
        conditionsField,
        excludeConditionsField,
        usersAndGroupsField,
        excludedUsersAndGroupsField
    ].filter((field) => !!field);

    existingFields.forEach((field) => {
        field.error = null;
    });

    const isEmptyInsight = existingFields.every((field) => isEmpty(field.value));
    if (isEmptyInsight) {
        const errorFields = existingFields.map((field) => `'${field.name}'`).join(' or ');
        const error = `${errorFields} must contain at least one item`;

        existingFields.forEach((field) => {
            field.error = error;
        });
    }
    existingFields.forEach((field) => {
        customInsightField.fields[field.id] = { ...field };
    });
};

export const validateNameUnique = (allFields, selectedTab, nameFieldId, caseSensitive) => {
    const nameToId = new Map();
    const isArrayFields = Array.isArray(allFields);

    for (const fieldId in allFields) {
        const currentFields = isArrayFields ? allFields[fieldId] : allFields[fieldId].fields;
        if (!currentFields) continue;
        if (fieldId === selectedTab) {
            currentFields[nameFieldId].error = null;
        }
        if (!currentFields[nameFieldId]) continue;
        const baseFieldName = currentFields[nameFieldId].value;
        const fieldName = caseSensitive ? baseFieldName : baseFieldName.toLowerCase();
        let fieldIds = [];
        if (nameToId.has(fieldName)) {
            fieldIds = nameToId.get(fieldName);
        }
        fieldIds.push(fieldId);
        nameToId.set(fieldName, fieldIds);
    }
    for (const [fieldName, fieldIds] of nameToId.entries()) {
        if (fieldIds.length > 1) {
            if (fieldIds.includes(selectedTab)) {
                const tabFields = isArrayFields ? allFields[selectedTab] : allFields[selectedTab].fields;
                tabFields[
                    nameFieldId
                ].error = `The name '${fieldName}' appears more than once, please enter unique name`;
            }
        }
    }
};

export const validateNodesRadiusAccountsUnique = (allFields, selectedTab) => {
    const selectedNode = allFields[selectedTab];
    const radiusUserName = getNodeRadiusUser(selectedNode);
    if (!radiusUserName) return;

    const allOtherNodes = { ...allFields };
    delete allOtherNodes[selectedTab];
    delete allOtherNodes[COMMON_FIELDS.LDAP_IP_OPTIONS.ID];
    const isUserInUse = Object.values(allOtherNodes).find((node) => getNodeRadiusUser(node) === radiusUserName);

    if (isUserInUse) {
        const radiusDomainId = getNodeDomainWithRadius(selectedNode);
        if (radiusDomainId) {
            selectedNode.fields[NODE_FIELDS.DOMAINS.ID].value[radiusDomainId][
                NODE_FIELDS.RADIUS_MACHINE_ACCOUNT.ID
            ].error = `The account '${radiusUserName}' is already used for another Node. Create a unique account for each Node.`;
        }
    }
};

const getNodeDomainWithRadius = (node) => {
    if (!node.fields[NODE_FIELDS.DOMAINS.ID]) {
        return null;
    }
    const nodeDomainsFields = node.fields[NODE_FIELDS.DOMAINS.ID].value;
    for (const nodeDomainId in nodeDomainsFields) {
        const nodeDomainFields = nodeDomainsFields[nodeDomainId];
        const userValue = nodeDomainFields[NODE_FIELDS.RADIUS_MACHINE_ACCOUNT.ID].value;
        if (userValue && userValue.user) {
            return nodeDomainId;
        }
    }
    return null;
};

const getNodeRadiusUser = (node) => {
    const radiusDomainId = getNodeDomainWithRadius(node);

    if (!radiusDomainId) {
        return '';
    }

    const nodeDomainsFields = node.fields[NODE_FIELDS.DOMAINS.ID].value;
    const userValue = nodeDomainsFields[radiusDomainId][NODE_FIELDS.RADIUS_MACHINE_ACCOUNT.ID].value;
    if (userValue && userValue.user) {
        return userValue.user;
    }

    return '';
};

export const validateAccountsUnique = (allFields, selectedTab, accountFieldsIds) => {
    const userToId = new Map();
    for (const nodeId of Object.keys(allFields)) {
        const currentFields = allFields[nodeId].fields;
        if (!currentFields) continue;
        if (nodeId === selectedTab) {
            for (const accountFieldId of accountFieldsIds) {
                currentFields[accountFieldId].error = null;
            }
        }
        for (const accountFieldId of accountFieldsIds) {
            if (!currentFields[accountFieldId]) continue;
            const fieldValue = currentFields[accountFieldId].value;
            const fieldUser = fieldValue ? fieldValue.user : null;
            if (fieldUser) {
                let idsObj = {};
                if (userToId.has(fieldUser)) {
                    idsObj = userToId.get(fieldUser);
                }
                if (!idsObj[nodeId]) {
                    idsObj[nodeId] = [];
                }
                idsObj[nodeId].push(accountFieldId);
                userToId.set(fieldUser, idsObj);
            }
        }
    }
    for (const [fieldUser, idsObj] of userToId.entries()) {
        if (Object.keys(idsObj).length > 1) {
            for (const nodeId in idsObj) {
                if (nodeId === selectedTab) {
                    for (const accountFieldId of idsObj[nodeId]) {
                        allFields[selectedTab].fields[
                            accountFieldId
                        ].error = `The account '${fieldUser}' is already used for another Node. Create a unique account for each Node.`;
                    }
                }
            }
        } else if (Object.keys(idsObj).length === 1) {
            const nodeId = Object.keys(idsObj)[0];
            if (nodeId === selectedTab) {
                const accountFieldsIds = idsObj[nodeId];
                if (accountFieldsIds.length > 1) {
                    for (const accountFieldIndex in idsObj[nodeId]) {
                        const accountFieldId = idsObj[nodeId][accountFieldIndex];
                        const duplicateFieldIndex = accountFieldIndex > 0 ? 0 : 1;
                        const duplicateFieldId = idsObj[nodeId][duplicateFieldIndex];
                        const duplicateFieldName = allFields[selectedTab].fields[duplicateFieldId].name;
                        allFields[selectedTab].fields[
                            accountFieldId
                        ].error = `The account '${fieldUser}' already used for a '${duplicateFieldName}'. Create a unique account.`;
                    }
                }
            }
        }
    }
};

export const validatePacketFwdProtocolsLimit = (failCloseField, selectiveFwdField, maxAddressesPerProtocol) => {
    const failCloseFields = failCloseField.fields[AD_PROTOCOLS.ID].value;
    const selectiveFwdFields = selectiveFwdField.fields[SELECTIVE_FORWARDING_FIELD.ID].value;

    const protocolsLimitCount = {};
    for (const protocolOption of Object.values(PACKET_FWD_PROTOCOL_OPTIONS)) {
        protocolsLimitCount[protocolOption.value] = 0;
    }

    for (const packetFwd of [...failCloseFields, ...selectiveFwdFields]) {
        let newAddressesCount = 0;
        if (packetFwd[PACKET_FWD_ADDRESS_FIELDS.ADDRESS_TYPE.ID].value === PACKET_FWD_ADDRESS_OPTIONS.range.value) {
            newAddressesCount = 2;
        } else {
            newAddressesCount = (packetFwd[PACKET_FWD_ADDRESS_FIELDS.SUBNET.ID].value || []).length;
        }

        for (const protocol of packetFwd[PACKET_FWD_ADDRESS_FIELDS.PROTOCOL.ID].value) {
            protocolsLimitCount[protocol] = (protocolsLimitCount[protocol] || 0) + newAddressesCount;
        }
    }

    const reachedLimitProtocols = [];
    for (const protocolOption of Object.values(PACKET_FWD_PROTOCOL_OPTIONS)) {
        if (protocolsLimitCount[protocolOption.value] > maxAddressesPerProtocol) {
            reachedLimitProtocols.push(protocolOption.label);
        }
    }

    let limitText = null;
    if (reachedLimitProtocols.length > 0) {
        limitText = `You have reached the joint Fail-Close/Selective Forwarding limit of IP ranges that protect these protocols: ${reachedLimitProtocols.join(
            ', '
        )}. Adding IP ranges beyond this limit may lower system performance.`;
    }
    failCloseField.fields[AD_PROTOCOLS.ID].warning = limitText;
    selectiveFwdField.fields[SELECTIVE_FORWARDING_FIELD.ID].warning = limitText;
};

export const validatePacketFwdAddress = (packetFwdFields) => {
    for (const packetFwd of packetFwdFields) {
        const subnet = packetFwd[PACKET_FWD_ADDRESS_FIELDS.SUBNET.ID];
        const rangeStart = packetFwd[PACKET_FWD_ADDRESS_FIELDS.RANGE_START.ID];
        const rangeEnd = packetFwd[PACKET_FWD_ADDRESS_FIELDS.RANGE_END.ID];
        if (packetFwd[PACKET_FWD_ADDRESS_FIELDS.ADDRESS_TYPE.ID].value === PACKET_FWD_ADDRESS_OPTIONS.range.value) {
            subnet.error = null;
            if (
                (rangeStart.error && rangeStart.error.includes(VALID_IP_ERROR)) ||
                (rangeEnd.error && rangeEnd.error.includes(VALID_IP_ERROR))
            ) {
                return;
            }
            if (!rangeStart.value && !rangeEnd.value) {
                rangeStart.error = `${rangeStart.name} and ${rangeEnd.name} fields must be set`;
            } else if (!rangeStart.value) {
                rangeStart.error = `${rangeStart.name} field must be set`;
            } else if (!rangeEnd.value) {
                rangeEnd.error = `${rangeEnd.name} field must be set`;
            } else if (compareVersions(rangeStart.value, rangeEnd.value) === 1) {
                rangeStart.error = `${rangeStart.name} field must be lower than ${rangeEnd.name}`;
            } else {
                rangeStart.error = null;
                rangeEnd.error = null;
            }
        } else {
            rangeStart.error = null;
            rangeEnd.error = null;
            if (subnet.error) {
                return;
            }
            if (!subnet.value || subnet.value.length === 0) {
                subnet.error = `${subnet.name} field must be set`;
            }
        }
    }
};

export const validatePacketFwdDuplications = (packetFwdFields) => {
    const alreadyExistErrStr = 'already exists for';
    const addressToIndexPerProtocol = {};
    for (const protocolOption of Object.values(PACKET_FWD_PROTOCOL_OPTIONS)) {
        addressToIndexPerProtocol[protocolOption.value] = {};
    }
    for (const packetFwdIndex in packetFwdFields) {
        const packetFwd = packetFwdFields[packetFwdIndex];
        const subnet = packetFwd[PACKET_FWD_ADDRESS_FIELDS.SUBNET.ID];
        const rangeStart = packetFwd[PACKET_FWD_ADDRESS_FIELDS.RANGE_START.ID];
        const rangeEnd = packetFwd[PACKET_FWD_ADDRESS_FIELDS.RANGE_END.ID];

        let addressStrs = [];
        let addressFieldId = PACKET_FWD_ADDRESS_FIELDS.SUBNET.ID;
        let addressFieldLabel = PACKET_FWD_ADDRESS_OPTIONS.subnet.label;
        if (packetFwd[PACKET_FWD_ADDRESS_FIELDS.ADDRESS_TYPE.ID].value === PACKET_FWD_ADDRESS_OPTIONS.range.value) {
            addressStrs = [`${rangeStart.value}-${rangeEnd.value}`];
            addressFieldId = PACKET_FWD_ADDRESS_FIELDS.RANGE_START.ID;
            addressFieldLabel = PACKET_FWD_ADDRESS_OPTIONS.range.label;
            subnet.error = null;
            if (rangeStart.error && rangeStart.error.includes(alreadyExistErrStr)) {
                rangeStart.error = null;
            }
            if (rangeEnd.error && rangeEnd.error.includes(alreadyExistErrStr)) {
                rangeEnd.error = null;
            }
        } else {
            addressStrs = subnet.value;
            rangeStart.error = null;
            rangeEnd.error = null;
            if (subnet.error && subnet.error.includes(alreadyExistErrStr)) {
                subnet.error = null;
            }
        }

        for (const addressStr of addressStrs) {
            for (const protocol of packetFwd[PACKET_FWD_ADDRESS_FIELDS.PROTOCOL.ID].value) {
                if (!addressToIndexPerProtocol[protocol][addressStr]) {
                    addressToIndexPerProtocol[protocol][addressStr] = [];
                }
                addressToIndexPerProtocol[protocol][addressStr].push(packetFwdIndex);
                if (addressToIndexPerProtocol[protocol][addressStr].length > 1) {
                    for (const fIndex of addressToIndexPerProtocol[protocol][addressStr]) {
                        packetFwdFields[fIndex][
                            addressFieldId
                        ].error = `${addressFieldLabel} '${addressStr}' ${alreadyExistErrStr} ${protocol}`;
                    }
                }
            }
        }
    }
};

export const validateFQDNWithSecureADSync = (nodeFields) => {
    for (const nodeDomainFields of nodeFields[NODE_FIELDS.DOMAINS.ID].value) {
        const ldapUrl = nodeDomainFields[NODE_FIELDS.LDAP_URL.ID];
        const dcAddress = nodeDomainFields[NODE_FIELDS.DC_ADDRESS.ID];
        const securedAdSync = nodeDomainFields[NODE_FIELDS.USE_SECURE_LDAP.ID];

        const secureLdapWarning = securedAdSync.value && ipPattern.test(ldapUrl.value);
        const secureDcWarning = securedAdSync.value && ipPattern.test(dcAddress.value);

        ldapUrl.notification = secureLdapWarning
            ? `To use secured AD Sync, enter an FQDN instead of an IP in the ${NODE_FIELDS.LDAP_URL.NAME} field.`
            : null;
        dcAddress.notification = secureDcWarning
            ? `To use LDAPS, enter an FQDN instead of an IP in the ${NODE_FIELDS.DC_ADDRESS.NAME} field.`
            : null;
    }
};

export const validateAdsyncNodesRequiredAndUnique = (allFields) => {
    const assignedNodesId = PROTECTED_DOMAIN_FIELDS.ASSIGNED_NODES.ID;
    const adsyncNodesId = PROTECTED_DOMAIN_FIELDS.ADSYNC_NODES.ID;

    const nodeDomainADSync = {};
    for (const domainId in allFields) {
        const domain = allFields[domainId].fields;
        if (!domain || !domain[adsyncNodesId]) {
            continue;
        }

        if (
            domain[assignedNodesId] &&
            domain[assignedNodesId].value &&
            domain[assignedNodesId].value.length > 0 &&
            domain[adsyncNodesId] &&
            (!domain[adsyncNodesId].value || domain[adsyncNodesId].value.length === 0)
        ) {
            domain[adsyncNodesId].error = 'Specify at least one Node to perform AD Sync on this domain.';
        } else {
            domain[adsyncNodesId].error = null;
        }

        for (const node of domain[adsyncNodesId].value) {
            if (!nodeDomainADSync[node]) {
                nodeDomainADSync[node] = [];
            }
            nodeDomainADSync[node].push(domainId);
        }
    }
};

export const validateAuthnMethodRequired = (ssoFields) => {
    const enabledAuthnContext = ssoFields[SSO_FIELDS.SSO_ENABLED_AUTHN_CONTEXT.ID];
    const authnContext = ssoFields[SSO_FIELDS.SSO_AUTHN_CONTEXT.ID];

    if (enabledAuthnContext.value && (!authnContext.value || !authnContext.value.length)) {
        authnContext.error = `${SSO_FIELDS.SSO_AUTHN_CONTEXT.NAME} is required`;
    } else {
        authnContext.error = null;
    }
};

export const mustHaveWallIfDc = (azFields, dcsFieldId, wallsFieldId) => {
    const dcsValue = azFields[dcsFieldId].value;
    const wallsValue = azFields[wallsFieldId].value;
    if (dcsValue.length !== 0 && wallsValue.length === 0) {
        azFields[wallsFieldId] = {
            ...azFields[wallsFieldId],
            error: MUST_HAVE_WALL_IF_DC_MSG
        };
    } else if (azFields[wallsFieldId].error === MUST_HAVE_WALL_IF_DC_MSG) {
        azFields[wallsFieldId] = {
            ...azFields[wallsFieldId],
            error: null
        };
    }
};

export const maxOneEmptyTab = (fields, fieldIdsToCheck) => {
    const emptyTabs = [];
    for (const tab in fields) {
        if (tab === SHARED_KEY) continue;
        let isEmpty = true;
        for (const fieldId of fieldIdsToCheck) {
            const value = fields[tab].fields[fieldId].value;
            if ((Array.isArray(value) && value.length !== 0) || (!Array.isArray(value) && value)) {
                isEmpty = false;
            }
        }

        if (isEmpty) {
            emptyTabs.push(tab);
        }
    }
    if (emptyTabs.length > 1) {
        for (const tab of emptyTabs) {
            fieldIdsToCheck.forEach((fieldId) => {
                fields[tab].fields[fieldId].error = MAX_ONE_EMPTY_TAB_MSG;
            });
        }
    } else {
        for (const tab in fields) {
            if (tab === SHARED_KEY) continue;
            fieldIdsToCheck.forEach((fieldId) => {
                if (fields[tab].fields[fieldId].error === MAX_ONE_EMPTY_TAB_MSG) {
                    fields[tab].fields[fieldId].error = null;
                }
            });
        }
    }
};

export const getNotification = (value, notifications) => {
    if (!notifications) {
        return null;
    }

    if (notifications.restartNeeded) {
        return `In order for the changes to take effect, please restart the server after saving`;
    }
    if (notifications.restartNeededIfDisabled && value === false) {
        return `Restart Admin Console after making this change for it to take effect.`;
    }
    if (notifications.restartAllNodes) {
        return 'Please note that changing this value will cause all nodes to restart';
    }
    if (notifications.restartNode) {
        return 'Please note that changing this value will cause this node to restart';
    }
    if (notifications.saveNeeded) {
        return 'Please note you need to press "Save" or "Save All Tabs" to apply your changes';
    }
    if (notifications.wizardSaveNeeded) {
        return 'Please note you need to press “Save” to apply your changes.';
    }
    if (notifications.refreshPageNeeded) {
        return 'In order for the changes to take effect, refresh this page after saving the certificate.';
    }
};
